import { ModelConceptAttribute } from 'src/containers/ProjectDiagram/types/data';
import { ClassHierarchy } from 'src/utils/getClassHierarchy';

export interface ModelConceptAttributeWithHierarchyData
  extends ModelConceptAttribute {
  depth: number; // for now, mainly used to determine if inherited/not inherited
  sourceClassIris: Set<string>; // classes from same depth of ancestry
}

export const getAllInheritedAttributes = ({
  classIri,
  hierarchyByIri,
  attributesByDomain,
}: {
  classIri?: string;
  hierarchyByIri: Record<string, ClassHierarchy>;
  attributesByDomain: Record<string, ModelConceptAttribute[]>;
}): Record<string, ModelConceptAttributeWithHierarchyData> => {
  if (!classIri) {
    return {};
  }

  const minDepthByVisitedClass: Record<string, number> = {};

  const attributesByIri: Record<
    string,
    ModelConceptAttributeWithHierarchyData
  > = {};

  const addAttribute =
    (currentIri: string, depth: number) =>
    (attribute: ModelConceptAttribute) => {
      // add the found attribute to the results, or replace existing
      // results if the attribute is inherited from a closer parent
      if (
        !attributesByIri[attribute.value] ||
        depth < attributesByIri[attribute.value].depth
      ) {
        attributesByIri[attribute.value] = {
          ...attribute,
          depth,
          sourceClassIris: new Set([currentIri]),
        };
      }
      // append the parent to this attribute if it shares the same
      // depth as other recognized parents
      else if (depth === attributesByIri[attribute.value].depth) {
        attributesByIri[attribute.value].sourceClassIris.add(currentIri);
      }
    };

  // depth starts at 1
  attributesByDomain[classIri]?.forEach(addAttribute(classIri, 1));

  Object.entries(hierarchyByIri[classIri]?.ancestors || {}).forEach(
    ([ancestor, depth]) => {
      const minDepth = minDepthByVisitedClass[ancestor];
      if (minDepth && minDepth <= depth) {
        return;
      }

      (attributesByDomain[ancestor] || []).forEach(
        addAttribute(ancestor, depth)
      );
    }
  );

  return attributesByIri;
};
